import { useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';

/**
 * Scrolls to the element matching the URL hash (e.g. `/docs#ci`). TanStack Router v1
 * has no built-in hash scrolling, and our pages render a loading skeleton before their
 * real content mounts — so the browser's native anchor scroll on load fires before the
 * target element exists and silently does nothing. This handles all three cases:
 *
 *  - Full-page load with a hash → run on mount, retry until the (skeleton-gated) target
 *    appears (rAF-bounded to ~1s so it never spins).
 *  - Same-page anchor click (`href="#ci"`) → the `hashchange` event re-runs the scroll.
 *  - SPA navigation to a new route + hash → the router location dependency re-runs it.
 *
 * Mounted once globally in the root so every route gets it.
 */
export function useScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    let raf = 0;

    const scrollToHash = () => {
      cancelAnimationFrame(raf);
      const id = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      if (!id) return;

      let tries = 0;
      const attempt = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView();
        } else if (tries++ < 60) {
          // Content may still be behind its mount skeleton; retry next frame.
          raf = requestAnimationFrame(attempt);
        }
      };
      attempt();
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => {
      window.removeEventListener('hashchange', scrollToHash);
      cancelAnimationFrame(raf);
    };
  }, [location.href]);
}
